import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExportColumnsDialog from "./ExportColumnsDialog";
import useExcelExport from "@Hooks/useExcelExport";

/**
 * Grid-toolbar entry point for the Excel export.
 *
 * It sits inside the toolbar rather than the page header so that the applied filter model and sort
 * are handed straight down from the table — the exported set cannot drift from what the grid is
 * showing.
 */
function ExcelExportButton({
  endpoint,
  filters = [],
  sortBy = "id",
  sortDir = "asc",
  fileNamePrefix = "exit-requests",
  storageKey,
  visibleRows,
  rowToExitRequestId,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { exporting, exportToExcel } = useExcelExport();

  if (!endpoint) {
    return null;
  }

  // Listings that filter in the browser cannot express their filters as a server query, so they
  // send the ids they are showing instead. The server intersects those with what the user may see,
  // so this narrows the export without granting access to anything.
  const clientSelectedIds =
    visibleRows && rowToExitRequestId
      ? [...new Set(visibleRows.map(rowToExitRequestId).filter((id) => id != null))]
      : null;

  const handleExport = async (columns) => {
    const succeeded = await exportToExcel({
      endpoint,
      filters,
      columns,
      ids: clientSelectedIds,
      sortBy,
      sortDir,
      fileNamePrefix,
    });
    // Keep the dialog open on failure so the chosen columns survive a retry.
    if (succeeded) {
      setDialogOpen(false);
    }
  };

  return (
    <Box className="toolbarLeftObject">
      <Button
        className="toolbarButton"
        size="small"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => setDialogOpen(true)}
      >
        Export Excel
      </Button>
      <ExportColumnsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onExport={handleExport}
        exporting={exporting}
        storageKey={storageKey}
      />
    </Box>
  );
}

export default ExcelExportButton;
